import styled from "@emotion/styled";
import { SearchIcon, SparklesIcon } from "lucide-react";
import type { FC, KeyboardEvent } from "react";
import { useEffect, useRef } from "react";
import { backgrounds, borders, colors, fonts, radius } from "../design-tokens";

const InputWrapper = styled.div`
  width: 100%;
`;

const InputContainer = styled.div<{ hasError?: boolean }>`
  border-radius: ${radius.md}px;
  ${props => (props.hasError
    ? `border: 1px solid ${colors.danger};`
    : borders.input)}
  ${backgrounds.glassy}
  display: flex;
  align-items: center;
  gap: ${radius.md}px;
  padding: ${radius.sm}px ${radius.sm}px ${radius.sm}px ${radius.lg}px;
  width: 100%;
  box-sizing: border-box;
  transition: all 200ms ease-in-out;

  &:focus-within {
    border-color: ${props =>
      props.hasError ? colors.danger : "var(--pollen-input-border-color, color-mix(in srgb, var(--pollen-accent-color, #00ABB8) 80%, transparent))"};
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: 12px;
  margin-top: 4px;
  padding-left: ${radius.lg}px;
`;

const StyledInput = styled.input`
  ${fonts.bodyFont}
  border: none;
  outline: none;
  background: transparent;
  font-size: 18px;
  color: ${colors.text};
  flex-grow: 1;
  width: 0;
  height: 30px;
  line-height: 30px;
  padding-block: 6px;
  cursor: text;

  &::placeholder {
    color: ${colors.muted};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  border-radius: ${radius.sm}px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 200ms ease-in-out;

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledSparkleIcon = styled(SparklesIcon)`
  width: 24px;
  height: 24px;
  color: ${colors.accentSoft};
  fill: ${colors.accentSoft};
  stroke-width: 1;
`;

const StyledSearchIcon = styled(SearchIcon)`
  width: 20px;
  height: 20px;
  color: ${colors.primary};
`;

type QueryInputProps = {
  placeholder?: string;
  autoFocus?: boolean;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  disabled?: boolean;
  error?: string | null;
};

export const QueryInput: FC<QueryInputProps> = ({
  placeholder = "Ask anything...",
  autoFocus = false,
  value,
  onChange,
  onSubmit,
  disabled = false,
  error = null,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timeout = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [autoFocus]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !disabled) {
      onSubmit(value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <InputWrapper>
        <InputContainer hasError={!!error}>
          <StyledSparkleIcon />
          <StyledInput
            ref={inputRef}
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
          />

          <ActionButton
            type="submit"
            disabled={!value.trim() || disabled}
            aria-label="Send message"
          >
            <StyledSearchIcon />
          </ActionButton>
        </InputContainer>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </InputWrapper>
    </form>
  );
};
